import React, { useEffect, useMemo, useState } from 'react'

import { CardReaderDeviceSettingsPanel } from '@/components/organisms/self-checkin/device-settings/card-reader-panel'
import { CashChangerDeviceSettingsPanel } from '@/components/organisms/self-checkin/device-settings/cash-changer-panel'
import {
  DeviceSettingsModalNavigationProps,
  DeviceSettingsTab,
  DeviceSettingsTabName,
} from '@/components/organisms/self-checkin/device-settings/modal-frame'
import { PrinterDeviceSettingsPanel } from '@/components/organisms/self-checkin/device-settings/printer-panel'
import { TerminalDeviceSettingsPanel } from '@/components/organisms/self-checkin/device-settings/terminal-panel'
import { checkActivePlugin, hasCardReaderPlugin, PluginCode, setHasSelfCheckinPlugin } from '@/libs/plugins'
import { TPlugin } from '@/models/plugin'

const ALL_TABS: DeviceSettingsTab[] = [
  { name: 'Printer', label: 'Printer' },
  { name: 'Terminal', label: 'Terminal' },
  { name: 'CashChanger', label: 'CashChanger' },
  { name: 'Card reader', label: 'Card reader' },
]

type Props = {
  hotelId: string
  plugins: TPlugin[]
  isOpen: boolean
  onClose: () => void
}

export const DeviceSettingsModal: React.FC<Props> = ({ hotelId, plugins, isOpen, onClose }) => {
  const tabs = useMemo(() => getAvailableTabs(plugins), [plugins])
  const [activeTab, setActiveTab] = useState<DeviceSettingsTabName | undefined>(() => tabs[0]?.name)

  useEffect(() => {
    if (!tabs.some(tab => tab.name === activeTab)) {
      setActiveTab(tabs[0]?.name)
    }
  }, [tabs, activeTab])

  if (!isOpen || !activeTab) {
    return null
  }

  const navigationProps: DeviceSettingsModalNavigationProps = {
    tabs,
    activeTab,
    onTabChange: setActiveTab,
    onClose,
  }

  if (activeTab === 'Terminal') {
    return <TerminalDeviceSettingsPanel hotelId={hotelId} {...navigationProps} />
  }
  if (activeTab === 'CashChanger') {
    return <CashChangerDeviceSettingsPanel hotelId={hotelId} {...navigationProps} />
  }
  if (activeTab === 'Card reader') {
    return <CardReaderDeviceSettingsPanel hotelId={hotelId} {...navigationProps} />
  }
  return <PrinterDeviceSettingsPanel hotelId={hotelId} {...navigationProps} />
}

const getAvailableTabs = (plugins: TPlugin[]): DeviceSettingsTab[] => {
  const hasSelfCheckinPlugin = setHasSelfCheckinPlugin(plugins)
  const hasPaymentTerminalPlugin = checkActivePlugin(plugins, PluginCode.PaymentTerminal)
  const hasCardReader = hasCardReaderPlugin(plugins)

  return ALL_TABS.filter(tab => {
    if (tab.name === 'Printer') return hasSelfCheckinPlugin
    if (tab.name === 'Terminal' || tab.name === 'CashChanger') return hasPaymentTerminalPlugin
    if (tab.name === 'Card reader') return hasCardReader
    return false
  })
}
